import React from 'react';
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-pro';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DiscountRenderCell from './DiscountRenderCell';
import GenericGridContent from '../../../components/datagridComponents/GenericGridContent';
import ActionsMenu from '../../../components/datagridComponents/ActionsMenu';
import { currencyDisplay } from '../../../helpers/helpers';

const BuildColumns = (currencyInfo: any, locale: string): GridColDef[] => [
  {
    field: 'name',
    flex: 0.5,
    minWidth: 100,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staffRoles.name',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) => (
      <GenericGridContent
        content={[
          {
            text: params.value,
            shouldTranslate: false,
            typographyProps: {
              variant: 'body2',
            },
          },
        ]}
      />
    ),
  },
  {
    field: 'description',
    flex: 0.7,
    minWidth: 120,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staffRoles.descriptionGrid',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
            shouldClipContent: true,
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) => (
      <GenericGridContent
        content={[
          {
            text: params.value,
            shouldTranslate: false,
            shouldClipContent: true,
            typographyProps: {
              variant: 'body2',
            },
          },
        ]}
      />
    ),
  },
  {
    field: 'adminOptsBackOffice',
    flex: 0.4,
    minWidth: 100,
    maxWidth: 150,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staffRoles.adminOptsBackOffice',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <CheckIcon
          key={`backoffice-access-enabled-${params?.row?.id}`}
          sx={{ color: 'success.main' }}
        />
      ) : (
        <CloseIcon
          key={`backoffice-access-disabled-${params?.row?.id}`}
          sx={{ color: 'icon.default' }}
        />
      ),
  },
  {
    field: 'globalTillAccess',
    flex: 0.4,
    minWidth: 100,
    maxWidth: 150,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staffRoles.globalTillAccess',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <CheckIcon key={`till-access-enabled-${params?.row?.id}`} sx={{ color: 'success.main' }} />
      ) : (
        <CloseIcon key={`till-access-disabled-${params?.row?.id}`} sx={{ color: 'icon.default' }} />
      ),
  },
  {
    field: 'adminOptsTillAdmin',
    flex: 0.4,
    minWidth: 120,
    maxWidth: 160,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staffRoles.adminOptsTillAdmin',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <CheckIcon
          key={`admin-till-access-enabled-${params?.row?.id}`}
          sx={{ color: 'success.main' }}
        />
      ) : (
        <CloseIcon
          key={`admin-till-access-disabled-${params?.row?.id}`}
          sx={{ color: 'icon.default' }}
        />
      ),
  },
  {
    field: 'discountOptsItemDiscount',
    flex: 0.4,
    minWidth: 100,
    maxWidth: 150,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staffRoles.discountOptsItemDiscount',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) => (
      <DiscountRenderCell
        params={params}
        checkField="discountOptsItemDiscount"
        numberLimit="discountOptsItemDiscountLimit"
        percentageLimit="discountOptsItemDiscountLimitPct"
      />
    ),
  },
  {
    field: 'discountOptsBasketDiscount',
    flex: 0.4,
    minWidth: 100,
    maxWidth: 150,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staffRoles.discountOptsBasketDiscount',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) => (
      <DiscountRenderCell
        params={params}
        checkField="discountOptsBasketDiscount"
        numberLimit="discountOptsBasketDiscountLimit"
        percentageLimit="discountOptsBasketDiscountLimitPct"
      />
    ),
  },
  {
    field: 'discountOptsRefunds',
    flex: 0.4,
    minWidth: 100,
    maxWidth: 125,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staffRoles.discountOptsRefunds',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) => (
      <DiscountRenderCell
        params={params}
        checkField="discountOptsRefunds"
        numberLimit="discountOptsRefundLimit"
      />
    ),
  },
  {
    field: 'customerOptsCreditLimit',
    flex: 0.4,
    minWidth: 110,
    maxWidth: 180,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staffRoles.customerOptsCreditLimit',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) => {
      if (params.value === null || params.value === undefined)
        return (
          <GenericGridContent
            content={[
              {
                text: 'screens.staffRoles.noLimit',
                shouldTranslate: true,
                typographyProps: {
                  variant: 'body2',
                },
              },
            ]}
          />
        );

      if (currencyInfo.isLoading) return null;

      return (
        <GenericGridContent
          content={[
            {
              text: currencyInfo.currencyISOCode
                ? currencyDisplay(params.value, locale, currencyInfo.currencyISOCode)
                : params.value,
              shouldTranslate: false,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      );
    },
  },
  {
    field: 'actions',
    minWidth: 100,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'common.actions',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    align: 'right',
    headerAlign: 'right',
    hideSortIcons: true,
    headerClassName: 'hideRightSeparator',
    cellClassName: 'actionsCell',
    renderCell: (params: GridRenderCellParams) => <ActionsMenu rowId={params.row.id.toString()} />,
  },
];

export default BuildColumns;
